/**
 * Pure TEA update for the Storyboard pointer drag (card candidate, threshold
 * promote, drag-over, drop planning).
 *
 * Shell: `useStoryboardDragStore.ts` (Pinia + window listeners, DOM hit
 * testing, workspace and database Cmds). DOM hit testing stays in the shell
 * (it chooses the drop target); this module decides what a drop means.
 */
import { storyboardInsertionSlot } from "../lib/storyboardInsertion";
import { assertNever } from "../lib/assertNever";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";
import {
  findActByFolder,
  actFolderForChapter,
  manuscriptItemKey,
  type ManuscriptItem,
  type ZwChapter,
} from "../lib/zwConfig";

/** Matches the outline's end-of-list drop zone: append ungrouped, outside any
 * act. Without it, a chapter trapped in a trailing act has nowhere to go. */
export const END_OF_MANUSCRIPT_DROP_TARGET = "__end_of_manuscript__";

/** What's being dragged. A flat (1-scene) chapter card drags as `"chapter"`,
 * same treatment the outline gives a collapsed chapter row, since there's no
 * separate scene affordance to drag on a card with nothing else in it.
 * `"unplaced-scene"`/`"unplaced-chapter"` are parked items picked up from the
 * sidebar's Unplaced list. `name` carries their `Notes/`-relative name in
 * place of `folder`/`sceneFile`, which parked items don't have.
 * `"arc-moment"` is an unlinked arc moment from the storyboard context panel.
 * `entryFile`/`momentId` identify which moment to link on drop. */
export type BoardDragSource = {
  key: string;
  kind:
    | "act"
    | "chapter"
    | "scene"
    | "unplaced-scene"
    | "unplaced-chapter"
    | "arc-moment"
    | "entity";
  folder: Maybe<string>;
  /** The act this card sits under, so a drop onto it can resolve the right
   * list. `nothing`/empty means ungrouped (or not a manuscript card at all). */
  actFolder: Maybe<string>;
  sceneFile: Maybe<string>;
  name: Maybe<string>;
  entryFile: Maybe<string>;
  momentId: Maybe<string>;
};

/** Every droppable surface: a flat chapter card, a scene card, a band's
 * append slot (past its last real card), or the sidebar's Unplaced tray
 * (drop a board card there to park it) and its individual entries (drop a
 * board card onto one to park alongside it). Mirrors `OutlineRow` in
 * shape/spirit but flattened, since the board's own layout (bands containing
 * a grid of cards) isn't a single linear list the way outline rows are. */
export type BoardCell =
  | { kind: "act"; folder: string }
  | { kind: "chapter"; folder: string }
  | { kind: "scene"; folder: string; sceneFile: string }
  | { kind: "chapter-tail"; folder: string }
  | { kind: "unplaced-tray" }
  | { kind: "unplaced-item"; name: string };

/** The cell under the pointer, resolved from the DOM by the shell. */
export type BoardHit = {
  /** Timeline boundaries use insert-before rather than card-slot semantics. */
  insertion: boolean;
  key: string;
  kind:
    | "act"
    | "chapter"
    | "scene"
    | "chapter-tail"
    | "chapter-boundary"
    | "end-of-manuscript"
    | "unplaced-tray"
    | "unplaced-item"
    | "arc-moment-tray";
  folder: Maybe<string>;
  /** The act this cell sits under, or `nothing` when it's ungrouped. */
  actFolder: Maybe<string>;
  sceneFile: Maybe<string>;
  name: Maybe<string>;
};

export const DRAG_START_THRESHOLD_PX = 4;

function absentString(m: Maybe<string>): boolean {
  return isNothing(m) || m.value.length === 0;
}

function hasString(m: Maybe<string>): m is { tag: "just"; value: string } {
  return isJust(m) && m.value.length > 0;
}

export type DragCandidate = {
  readonly info: BoardDragSource;
  readonly startX: number;
  readonly startY: number;
};

export type StoryboardDragModel = {
  /** Pointer is down on a card but hasn't moved past the threshold yet. */
  readonly candidate: Maybe<DragCandidate>;
  readonly dragFrom: Maybe<BoardDragSource>;
  /** Key of the cell currently highlighted as the drop target. */
  readonly dragOver: Maybe<string>;
  /** Kind of `dragFrom` while a drag is live (set and cleared with it). */
  readonly dragKind: Maybe<BoardDragSource["kind"]>;
  /** Set by any gesture that became a drag; read-and-cleared by the click
   * handler that follows, so a drop doesn't also count as a click. */
  readonly gestureWasDrag: boolean;
};

export const initialStoryboardDragModel: StoryboardDragModel = {
  candidate: nothing,
  dragFrom: nothing,
  dragOver: nothing,
  dragKind: nothing,
  gestureWasDrag: false,
};

export type DropPoint = { readonly x: number; readonly y: number };

export type StoryboardDragMsg =
  | {
      readonly type: "CardPointerDown";
      readonly info: BoardDragSource;
      readonly button: number;
      readonly x: number;
      readonly y: number;
      /** Pointer landed inside an input / textarea / contenteditable. */
      readonly targetIsEditable: boolean;
    }
  | {
      readonly type: "CandidatePointerMoved";
      readonly x: number;
      readonly y: number;
    }
  /** Candidate pointerup or pointercancel before the threshold. */
  | { readonly type: "CandidatePointerEnded" }
  | {
      readonly type: "DragPointerMoved";
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly type: "DragOverMeasured";
      readonly hit: Maybe<BoardHit>;
      /** Scene path the arc moment would link to, if `hit` is a valid target. */
      readonly arcScenePath: Maybe<string>;
    }
  /** Drag pointerup (with a point) or pointercancel (without one). */
  | { readonly type: "DragPointerEnded"; readonly point: Maybe<DropPoint> }
  | {
      readonly type: "DropResolved";
      readonly from: BoardDragSource;
      readonly hit: BoardHit;
      readonly arcScenePath: Maybe<string>;
      readonly chapters: readonly ZwChapter[];
      readonly manuscript: ManuscriptItem[];
    }
  | { readonly type: "GestureConsumed" }
  | { readonly type: "Cleanup" }
  /** Plain setters (kept so the store's drag refs stay writable). */
  | { readonly type: "DragFromSet"; readonly value: Maybe<BoardDragSource> }
  | { readonly type: "DragOverSet"; readonly value: Maybe<string> }
  | {
      readonly type: "DragKindSet";
      readonly value: Maybe<BoardDragSource["kind"]>;
    };

export type StoryboardDragCmd =
  | {
      readonly kind: "linkEntity";
      readonly entryFile: string;
      readonly scenePath: string;
    }
  | { readonly kind: "addCandidateListeners" }
  | { readonly kind: "removeCandidateListeners" }
  | { readonly kind: "addDragListeners" }
  | { readonly kind: "removeDragListeners" }
  | { readonly kind: "measureDragOver"; readonly x: number; readonly y: number }
  | {
      readonly kind: "resolveDrop";
      readonly from: BoardDragSource;
      readonly point: Maybe<DropPoint>;
      readonly overKey: Maybe<string>;
    }
  | {
      readonly kind: "moveChapterToSlot";
      readonly chapterFolder: string;
      readonly actFolder: Maybe<string>;
      readonly beforeFolder: Maybe<string>;
    }
  | {
      readonly kind: "moveAct";
      readonly actFolder: string;
      readonly before: Maybe<string>;
    }
  | {
      readonly kind: "reorderScenes";
      readonly from: { readonly folder: string; readonly sceneFile: string };
      readonly to: {
        readonly folder: string;
        readonly sceneFile: Maybe<string>;
      };
    }
  | {
      readonly kind: "parkScene";
      readonly folder: string;
      readonly sceneFile: string;
    }
  | { readonly kind: "parkChapter"; readonly folder: string }
  /** Unpark into a chapter (always appends), then optionally reposition
   * before `reorderBefore`. One Cmd so the two steps stay sequential. */
  | {
      readonly kind: "unparkScene";
      readonly parkedName: string;
      readonly toFolder: string;
      readonly reorderBefore: Maybe<string>;
    }
  | {
      readonly kind: "unparkChapter";
      readonly parkedName: string;
      readonly atIndex: number;
    }
  | {
      readonly kind: "unlinkArcMoment";
      readonly entryFile: string;
      readonly momentId: string;
    }
  | {
      readonly kind: "linkArcMoment";
      readonly entryFile: string;
      readonly momentId: string;
      readonly scenePath: string;
    };

/** True when the next move would promote the candidate into a drag. The shell
 * uses this to call `preventDefault` on that very event. */
export function candidatePromotes(
  model: StoryboardDragModel,
  x: number,
  y: number,
): boolean {
  if (isNothing(model.candidate)) return false;
  const dx = x - model.candidate.value.startX;
  const dy = y - model.candidate.value.startY;
  return Math.hypot(dx, dy) >= DRAG_START_THRESHOLD_PX;
}

function planChapterDrop(
  from: BoardDragSource,
  to: BoardHit,
  manuscript: ManuscriptItem[],
): readonly StoryboardDragCmd[] {
  if (hasString(from.folder) === false) return [];
  const chapterFolder = from.folder.value;
  if (to.kind === "chapter-boundary") {
    const sourceAct = actFolderForChapter(manuscript, chapterFolder);
    const beforeFolder = isNothing(sourceAct)
      ? storyboardInsertionSlot(
          manuscript.map((item) => ({ file: manuscriptItemKey(item) })),
          chapterFolder,
          to.folder,
        )
      : to.folder;
    return [
      {
        kind: "moveChapterToSlot",
        chapterFolder,
        actFolder: nothing,
        beforeFolder,
      },
    ];
  }

  // Append ungrouped at the end: the only reliable way to pull a chapter
  // out of a trailing act when every other card still sits under an act.
  if (to.kind === "end-of-manuscript") {
    return [
      {
        kind: "moveChapterToSlot",
        chapterFolder,
        actFolder: nothing,
        beforeFolder: nothing,
      },
    ];
  }
  if (
    absentString(to.folder) ||
    (isJust(from.folder) &&
      isJust(to.folder) &&
      from.folder.value === to.folder.value)
  ) {
    return [];
  }
  // Dropping onto an act's own header appends to that act. It's the only
  // gesture that reaches the end of an act's chapter list.
  if (to.kind === "act" && isJust(to.folder)) {
    return [
      {
        kind: "moveChapterToSlot",
        chapterFolder,
        actFolder: to.folder,
        beforeFolder: nothing,
      },
    ];
  }
  return [
    {
      kind: "moveChapterToSlot",
      chapterFolder,
      actFolder: to.actFolder,
      beforeFolder: to.folder,
    },
  ];
}

/** An act drags as one unit, chapters included, so it only ever lands in a
 * top-level slot: before another act, or before the act/chapter span the
 * drop target belongs to. */
function planActDrop(
  from: BoardDragSource,
  to: BoardHit,
): readonly StoryboardDragCmd[] {
  if (hasString(from.folder) === false) return [];
  const actFolder = from.folder.value;
  if (to.kind === "end-of-manuscript") {
    return [{ kind: "moveAct", actFolder, before: nothing }];
  }
  const before: Maybe<string> =
    to.kind === "act"
      ? to.folder
      : isJust(to.actFolder)
        ? to.actFolder
        : to.folder;
  if (
    absentString(before) ||
    (isJust(from.folder) &&
      isJust(before) &&
      before.value === from.folder.value)
  ) {
    return [];
  }
  return [{ kind: "moveAct", actFolder, before }];
}

function adjustedSceneFile(
  sourceFile: string,
  folder: string,
  base: Maybe<string>,
  chapters: readonly ZwChapter[],
): Maybe<string> {
  const chapter = toMaybe(chapters.find((item) => item.folder === folder));
  if (isNothing(chapter)) return base;
  return storyboardInsertionSlot(chapter.value.scenes, sourceFile, base);
}

function planSceneDrop(
  from: BoardDragSource,
  to: BoardHit,
  chapters: readonly ZwChapter[],
): readonly StoryboardDragCmd[] {
  if (
    hasString(from.folder) === false ||
    hasString(from.sceneFile) === false ||
    hasString(to.folder) === false
  ) {
    return [];
  }
  // Acts hold no prose, so they're inert for scene drags.
  if (to.kind === "act") return [];
  if (
    isJust(from.folder) &&
    isJust(to.folder) &&
    from.folder.value === to.folder.value &&
    isJust(from.sceneFile) &&
    isJust(to.sceneFile) &&
    from.sceneFile.value === to.sceneFile.value
  ) {
    return [];
  }
  const baseSceneFile = to.kind === "scene" ? to.sceneFile : nothing;
  const adjusting =
    to.insertion === true && from.folder.value === to.folder.value;
  const toSceneFile = adjusting
    ? adjustedSceneFile(
        from.sceneFile.value,
        to.folder.value,
        baseSceneFile,
        chapters,
      )
    : baseSceneFile;
  if (
    adjusting &&
    isJust(toSceneFile) &&
    toSceneFile.value === from.sceneFile.value
  ) {
    return [];
  }
  return [
    {
      kind: "reorderScenes",
      from: { folder: from.folder.value, sceneFile: from.sceneFile.value },
      to: { folder: to.folder.value, sceneFile: toSceneFile },
    },
  ];
}

/** A board card (scene or flat/band chapter) dropped onto the Unplaced tray or
 * one of its existing entries: same "park" a chapter's own Park action would
 * do, just reached by dragging instead. */
function planParkDrop(from: BoardDragSource): readonly StoryboardDragCmd[] {
  if (from.kind === "scene" && isJust(from.folder) && isJust(from.sceneFile)) {
    return [
      {
        kind: "parkScene",
        folder: from.folder.value,
        sceneFile: from.sceneFile.value,
      },
    ];
  }
  if (from.kind === "chapter" && isJust(from.folder)) {
    return [{ kind: "parkChapter", folder: from.folder.value }];
  }
  return [];
}

/** An Unplaced entry dropped back onto the board: restores it at the exact
 * slot it landed on, same exact-slot semantics a normal card drag uses (insert
 * before the scene/chapter it's dropped on; a chapter's tail or empty space
 * around it appends instead).
 *
 * Unparking a scene always appends with its original name (the backend's third
 * argument is a *rename* target, not a position). Then the shell repositions
 * it with the same exact-slot reorder a normal scene drag uses. */
function planUnparkDrop(
  from: BoardDragSource,
  to: BoardHit,
  chapters: readonly ZwChapter[],
  manuscript: ManuscriptItem[],
): readonly StoryboardDragCmd[] {
  if (hasString(from.name) === false) return [];
  const parkedName = from.name.value;
  if (from.kind === "unplaced-scene") {
    if (hasString(to.folder) === false) return [];
    const toFolder = to.folder.value;
    const targetSceneFile: Maybe<string> =
      to.kind === "scene"
        ? to.sceneFile
        : to.kind === "chapter"
          ? toMaybe(
              chapters.find((c) => c.folder === toFolder)?.scenes[0]?.file,
            )
          : nothing; // chapter-tail: append is already the right slot
    const reorderBefore: Maybe<string> =
      isJust(targetSceneFile) &&
      targetSceneFile.value.length > 0 &&
      targetSceneFile.value !== parkedName
        ? targetSceneFile
        : nothing;
    return [{ kind: "unparkScene", parkedName, toFolder, reorderBefore }];
  }
  if (from.kind === "unplaced-chapter") {
    // Dropping on an act aims at that act's span, so resolve to its first
    // chapter's flat position. The holding pen only speaks flat indices.
    const targetFolder =
      to.kind === "act" && isJust(to.folder)
        ? (() => {
            const actM = findActByFolder(manuscript, to.folder.value);
            return isJust(actM)
              ? (actM.value.chapters[0]?.folder ?? null)
              : null;
          })()
        : hasString(to.folder)
          ? to.folder.value
          : null;
    const targetIndex =
      targetFolder !== null && targetFolder.length > 0
        ? chapters.findIndex((c) => c.folder === targetFolder)
        : -1;
    return [
      {
        kind: "unparkChapter",
        parkedName,
        atIndex: targetIndex < 0 ? chapters.length : targetIndex,
      },
    ];
  }
  return [];
}

function arcMomentIds(
  from: BoardDragSource,
): Maybe<{ entryFile: string; momentId: string }> {
  const entryFile = from.entryFile;
  const momentId = from.momentId;
  if (
    isNothing(entryFile) ||
    entryFile.value.length === 0 ||
    isNothing(momentId) ||
    momentId.value.length === 0
  ) {
    return nothing;
  }
  return just({ entryFile: entryFile.value, momentId: momentId.value });
}

/** What a resolved drop should do. Same branch order as the pre-TEA store. */
export function planDrop(
  from: BoardDragSource,
  to: BoardHit,
  arcScenePath: Maybe<string>,
  chapters: readonly ZwChapter[],
  manuscript: ManuscriptItem[],
): readonly StoryboardDragCmd[] {
  const fromIsUnplaced =
    from.kind === "unplaced-scene" || from.kind === "unplaced-chapter";
  const hitIsUnplaced =
    to.kind === "unplaced-tray" || to.kind === "unplaced-item";

  if (fromIsUnplaced && hitIsUnplaced) {
    // Dropped one Unplaced entry onto another. No reordering in the holding
    // pen, nothing to do.
    return [];
  }
  if (from.kind === "entity") {
    if (
      to.kind !== "scene" ||
      isNothing(arcScenePath) ||
      isNothing(from.entryFile)
    )
      return [];
    return [
      {
        kind: "linkEntity",
        entryFile: from.entryFile.value,
        scenePath: arcScenePath.value,
      },
    ];
  }
  if (from.kind === "arc-moment") {
    const ids = arcMomentIds(from);
    if (isNothing(ids)) return [];
    if (to.kind === "arc-moment-tray") {
      return [{ kind: "unlinkArcMoment", ...ids.value }];
    }
    if (isNothing(arcScenePath)) return [];
    return [
      { kind: "linkArcMoment", ...ids.value, scenePath: arcScenePath.value },
    ];
  }
  if (hitIsUnplaced) return planParkDrop(from);
  if (fromIsUnplaced) return planUnparkDrop(from, to, chapters, manuscript);
  if (from.kind === "scene") return planSceneDrop(from, to, chapters);
  if (from.kind === "act") return planActDrop(from, to);
  return planChapterDrop(from, to, manuscript);
}

/** The drop-target key highlighted while dragging over `hit`. */
function dragOverKey(
  from: BoardDragSource,
  hit: Maybe<BoardHit>,
  arcScenePath: Maybe<string>,
): Maybe<string> {
  if (from.kind === "entity") {
    return isJust(hit) && hit.value.kind === "scene" && isJust(arcScenePath)
      ? just(hit.value.key)
      : nothing;
  }
  if (from.kind === "arc-moment") {
    if (isJust(hit) && hit.value.kind === "arc-moment-tray") {
      return just(hit.value.key);
    }
    return isJust(hit) && isJust(arcScenePath) ? just(hit.value.key) : nothing;
  }
  return isJust(hit) && hit.value.key !== from.key
    ? just(hit.value.key)
    : nothing;
}

export function updateStoryboardDrag(
  model: StoryboardDragModel,
  msg: StoryboardDragMsg,
): readonly [StoryboardDragModel, readonly StoryboardDragCmd[]] {
  switch (msg.type) {
    case "CardPointerDown":
      // Same interactive-control opt-out as `useOutlineDrag.ts`: a defensive
      // backstop even though every caller wires this to a dedicated grip.
      if (msg.button !== 0 || msg.targetIsEditable) return [model, []];
      return [
        {
          ...model,
          candidate: just({ info: msg.info, startX: msg.x, startY: msg.y }),
        },
        [{ kind: "addCandidateListeners" }],
      ];
    case "CandidatePointerMoved": {
      if (candidatePromotes(model, msg.x, msg.y) === false) return [model, []];
      if (isNothing(model.candidate)) return [model, []];
      return [
        {
          candidate: nothing,
          dragFrom: just(model.candidate.value.info),
          dragOver: nothing,
          dragKind: just(model.candidate.value.info.kind),
          gestureWasDrag: true,
        },
        [
          { kind: "removeCandidateListeners" },
          { kind: "addDragListeners" },
          { kind: "measureDragOver", x: msg.x, y: msg.y },
        ],
      ];
    }
    case "CandidatePointerEnded":
      return [
        { ...model, candidate: nothing },
        [{ kind: "removeCandidateListeners" }],
      ];
    case "DragPointerMoved":
      if (isNothing(model.dragFrom)) return [model, []];
      return [model, [{ kind: "measureDragOver", x: msg.x, y: msg.y }]];
    case "DragOverMeasured":
      if (isNothing(model.dragFrom)) return [model, []];
      return [
        {
          ...model,
          dragOver: dragOverKey(
            model.dragFrom.value,
            msg.hit,
            msg.arcScenePath,
          ),
        },
        [],
      ];
    case "DragPointerEnded": {
      const from = model.dragFrom;
      const next: StoryboardDragModel = {
        ...model,
        dragFrom: nothing,
        dragOver: nothing,
        dragKind: nothing,
        gestureWasDrag: isJust(from) ? true : model.gestureWasDrag,
      };
      if (isNothing(from)) return [next, [{ kind: "removeDragListeners" }]];
      return [
        next,
        [
          {
            kind: "resolveDrop",
            from: from.value,
            point: msg.point,
            overKey: model.dragOver,
          },
          { kind: "removeDragListeners" },
        ],
      ];
    }
    case "DropResolved":
      return [
        model,
        planDrop(
          msg.from,
          msg.hit,
          msg.arcScenePath,
          msg.chapters,
          msg.manuscript,
        ),
      ];
    case "GestureConsumed":
      return [{ ...model, gestureWasDrag: false }, []];
    case "Cleanup":
      return [
        { ...model, candidate: nothing },
        [{ kind: "removeCandidateListeners" }, { kind: "removeDragListeners" }],
      ];
    case "DragFromSet":
      return [{ ...model, dragFrom: msg.value }, []];
    case "DragOverSet":
      return [{ ...model, dragOver: msg.value }, []];
    case "DragKindSet":
      return [{ ...model, dragKind: msg.value }, []];
    default:
      return assertNever(msg);
  }
}

export const updateStoryboardDragFn: UpdateFn<
  StoryboardDragModel,
  StoryboardDragMsg,
  StoryboardDragCmd
> = updateStoryboardDrag;
